import { useCallback, useEffect, useRef, useState } from "react";
import { billingOnboard, listTeamCards } from "./billingApi";
import { fetchIsMasterAccount } from "./billing-profile/billingProfileGate";
import type { BillingAddressFormData, PaymentMethod } from "./types";

interface UseCheckoutPaymentReturn {
  paymentMethods: PaymentMethod[];
  defaultBillingAddress: BillingAddressFormData | null;
  selectedPaymentMethodId: number | "new";
  selectPaymentMethod: (id: number | "new") => void;
  saveCard: boolean;
  setSaveCard: (v: boolean) => void;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  selectedCard: PaymentMethod | null;
  refetch: () => Promise<PaymentMethod[]>;
  /** Master Account team: cards come from (and are added to) the team-scoped endpoints. */
  isMasterAccount: boolean;
  /** False until /me answered — adding a card before then could hit the wrong vault. */
  accountResolved: boolean;
  /** Team card limit reached (Master Account only); legacy stays true. */
  canAddMore: boolean;
}

export function useCheckoutPayment(): UseCheckoutPaymentReturn {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [defaultBillingAddress, setDefaultBillingAddress] = useState<BillingAddressFormData | null>(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | "new">("new");
  const [saveCard, setSaveCard] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMasterAccount, setIsMasterAccount] = useState(false);
  const [accountResolved, setAccountResolved] = useState(false);
  const [canAddMore, setCanAddMore] = useState(true);
  const fetchedRef = useRef(false);
  const masterAccountRef = useRef<boolean | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const fetchPaymentMethods = useCallback(async (): Promise<PaymentMethod[]> => {
    try {
      if (masterAccountRef.current === null) {
        masterAccountRef.current = await fetchIsMasterAccount();
        setIsMasterAccount(masterAccountRef.current);
        setAccountResolved(true);
      }
      if (masterAccountRef.current) {
        // Team cards + authorized org cards (AC182). No per-user onboarding —
        // the team's Stripe customer is created server-side on first use.
        const data = await listTeamCards();
        const methods = data.cards || [];
        setPaymentMethods(methods);
        setCanAddMore(data.canAddMore ?? true);
        setDefaultBillingAddress(null);
        return methods;
      }
      const data = await billingOnboard();
      const methods = data.savedPaymentMethods || [];
      setPaymentMethods(methods);
      setDefaultBillingAddress(data.defaultBillingAddress ?? null);
      return methods;
    } catch (err) {
      console.error("Failed to load payment methods:", err);
      setError(
        masterAccountRef.current === null
          ? "Could not load your account. Please try again."
          : "Failed to load payment methods. Please try again."
      );
      return [];
    }
  }, []);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      const methods = await fetchPaymentMethods();
      if (methods.length > 0) {
        const primary = methods.find((m) => m.isPrimary && !m.isExpired);
        const firstValid = methods.find((m) => !m.isExpired);
        const preselect = primary || firstValid;
        if (preselect) {
          setSelectedPaymentMethodId(preselect.id);
        }
      }
      setIsLoading(false);
    })();
  }, [fetchPaymentMethods]);

  const refetch = useCallback(async (): Promise<PaymentMethod[]> => {
    return fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const selectPaymentMethod = useCallback((id: number | "new") => {
    setSelectedPaymentMethodId(id);
    setError(null);
  }, []);

  const selectedCard =
    selectedPaymentMethodId !== "new" ? paymentMethods.find((m) => m.id === selectedPaymentMethodId) || null : null;

  return {
    paymentMethods,
    defaultBillingAddress,
    selectedPaymentMethodId,
    selectPaymentMethod,
    saveCard,
    setSaveCard,
    isLoading,
    error,
    clearError,
    selectedCard,
    refetch,
    isMasterAccount,
    accountResolved,
    canAddMore,
  };
}
